import { ConnectionPositionPair } from '@angular/cdk/overlay';
import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  output,
  inject,
  input,
} from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';

import moment from 'moment-timezone';

import { I18nService } from '@core/i18n-legacy';
import { timeValidator } from '@core/time-legacy';
import { TimePickerDropdownComponent } from '@core/time-ui';
import {
  RtStatefulComponent,
  InputComponent,
  OverlayOriginDirective,
  ConnectedOverlayDirective,
} from '@core/user-interface-legacy';

type State = {
  dropdownOpen: boolean;
};

@Component({
  selector: 'rt-schedule-time-picker',
  templateUrl: 'schedule-time-picker.component.html',
  styleUrls: ['./schedule-time-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputComponent,
    OverlayOriginDirective,
    ReactiveFormsModule,
    ConnectedOverlayDirective,
    TimePickerDropdownComponent,
  ],
})
export class ScheduleTimePickerComponent extends RtStatefulComponent<State> {
  private readonly i18n = inject(I18nService);
  protected readonly cdRef: ChangeDetectorRef;

  public readonly dropdownPositions: ConnectionPositionPair[] = [
    new ConnectionPositionPair(
      { originX: 'start', originY: 'bottom' },
      { overlayX: 'start', overlayY: 'top' },
    ),
  ];
  public readonly dropdownWidth: number = 120;

  public readonly time = input<string>(undefined!);
  public readonly dropdownMaxHeight = input<number>(300);
  public readonly dark = input<boolean>(true);
  public readonly timeInterval = input<number>(15);
  public readonly placeholder = input<string>(
    this.i18n.translate('app.interview.schedule_dialog.time_placeholder'),
  );
  public readonly fullWidth = input<boolean>(false);
  public readonly invalid = input<boolean>(false);

  public timeChange = output<string>();

  public timeControl: UntypedFormControl;

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      dropdownOpen: false,
    });
  }

  public ngOnInit(): void {
    this.timeControl = new UntypedFormControl(
      moment(this.time(), 'HH:mm').format('LT'),
      timeValidator,
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { time } = changes;

    if (time?.currentValue && this.timeControl) {
      this.timeControl.setValue(moment(time.currentValue, 'HH:mm').format('LT'));
    }
  }

  public toggleDropdown(state: boolean | undefined = undefined): void {
    this.setState({
      dropdownOpen: state === undefined ? !this.state.dropdownOpen : state,
    });
  }

  public onTimeChange(time: string): void {
    this.toggleDropdown(false);
    if (moment(time, 'LT').isValid()) {
      this.timeChange.emit(moment(time, 'LT').format('HH:mm'));
    }
  }
}
